import { Stack } from "@mantine/core"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { FilterBuilderWrapper } from "~/components/table/FilterBuilderWrapper"
import {
  FindIngestionsFiltersDto,
  findIngestionsFiltersDtoStatusEnum,
  findIngestionsFiltersDtoTypeEnum,
} from "~/gen/api"
import { useFiltersOptions } from "~/pages/Brand/DataTransfer/Import/hooks/useFilterOptions"

type IngestionListFiltersProps = {
  filters: FindIngestionsFiltersDto
  setFilters: React.Dispatch<React.SetStateAction<FindIngestionsFiltersDto>>
}

export const IngestionListFilters = ({
  filters,
  setFilters,
}: IngestionListFiltersProps) => {
  const { t } = useTranslation("dataTransfer")

  const filtersOptions = useFiltersOptions()

  const { userEmails, createdAtOptions, fileNameOptions } = filtersOptions

  const emailFilterOptions = useMemo(() => {
    return userEmails.map((email) => ({
      value: email,
      label: email,
      key: email,
    }))
  }, [userEmails])

  const createdAtFilterOptions = useMemo(() => {
    return createdAtOptions.map((createdAt) => ({
      label: dayjs(createdAt).format("l"),
      value: createdAt.toString(),
      key: createdAt,
    }))
  }, [createdAtOptions])

  const fileNameFilterOptions = useMemo(() => {
    return fileNameOptions.map((fileName) => ({
      value: fileName,
      label: fileName,
      key: fileName,
    }))
  }, [fileNameOptions])

  return (
    <Stack>
      <FilterBuilderWrapper
        title={t("import.list.filters.title")}
        searchPlaceholder={t("import.list.filters.search")}
        filters={filters}
        setFilters={setFilters}
        options={[
          {
            label: t("import.list.table.columns.status.header"),
            key: "status",
            type: "multiSelect",
            options: [
              {
                value: findIngestionsFiltersDtoStatusEnum.PENDING,
                label: t("import.list.filters.pending"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.ERROR,
                label: t("import.list.filters.error"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.FAILED,
                label: t("import.list.filters.failed"),
              },
              {
                value: findIngestionsFiltersDtoStatusEnum.SUCCESS,
                label: t("import.list.filters.success"),
              },
            ],
          },
          {
            label: t("import.list.table.columns.user.header"),
            key: "userEmail",
            options: emailFilterOptions,
            type: "multiSelect",
          },
          {
            label: t("import.list.table.columns.date.header"),
            key: "createdAt",
            options: createdAtFilterOptions,
            disableSort: true,
            type: "dateRange",
          },
          {
            label: t("import.list.table.columns.type.header"),
            key: "type",
            type: "multiSelect",
            options: [
              {
                label: t("import.legacy.templateCard.title"),
                value: findIngestionsFiltersDtoTypeEnum.PRODUCT,
              },
              {
                label: t("import.businessPartner.templateCard.title"),
                value: findIngestionsFiltersDtoTypeEnum.BUSINESS_PARTNER,
              },
              {
                label: t("import.ecoModulation.templateCard.title"),
                value: findIngestionsFiltersDtoTypeEnum.ECO_MODULATION,
              },
            ],
          },
          {
            label: t("import.list.table.columns.file.header"),
            key: "fileName",
            options: fileNameFilterOptions,
            type: "multiSelect",
          },
        ]}
      />
    </Stack>
  )
}
